import React from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiPanel, EuiSpacer, EuiEmptyPrompt } from '@elastic/eui'

import { ReportCampaignDimension, ReportCampaign } from 'api/interfaces'
import ReportDataGrid, { ReportDataGridColumn, ReportDataGridSorting } from 'features/reports/ReportDataGrid'
import { formatNumber, formatPercentage } from 'utils/Formatters'

import { EmptyPromptComponent, LoadingComponent, TitleComponent } from './FormattingComponents'

interface DomainPerformanceProps {
  placementData?: ReportCampaignDimension[]
  campaignData?: ReportCampaign[]
  isLoading: boolean
}

interface AggregatedDomainData {
  domain: string
  views: number
  clicks: number
  ctr: number
}

const DomainPerformance: React.FC<DomainPerformanceProps> = ({ placementData, campaignData, isLoading }) => {
  const title = 'Domain Data'

  if (isLoading) {
    return <LoadingComponent title={title} />
  }

  if (!placementData || placementData.length <= 0) {
    return <EmptyPromptComponent title={title} body='There is no domain data for the selected period.' />
  }

  const aggregateDomainData = (data: ReportCampaignDimension[], totalCampaignData?: ReportCampaign[]): AggregatedDomainData[] => {
    const domainMap = new Map<string, AggregatedDomainData>()

    data.forEach(item => {
      const domain = item.dimensionValue
      const existing = domainMap.get(domain)

      if (existing) {
        existing.views += item.views
        existing.clicks += item.clicks
      } else {
        domainMap.set(domain, {
          domain,
          views: item.views,
          clicks: item.clicks,
          ctr: 0
        })
      }
    })

    const aggregatedDomains = Array.from(domainMap.values())
      .filter(domain => domain.views > 0)
      .map(domain => ({
        ...domain,
        ctr: domain.views > 0 ? (domain.clicks / domain.views) * 100 : 0
      }))

    if (totalCampaignData && totalCampaignData.length > 0) {
      const totalCampaignViews = totalCampaignData.reduce((sum, item) => sum + item.views, 0)
      const totalCampaignClicks = totalCampaignData.reduce((sum, item) => sum + item.clicks, 0)

      const totalDomainViews = aggregatedDomains.reduce((sum, domain) => sum + domain.views, 0)
      const totalDomainClicks = aggregatedDomains.reduce((sum, domain) => sum + domain.clicks, 0)

      const notSetViews = totalCampaignViews - totalDomainViews
      const notSetClicks = totalCampaignClicks - totalDomainClicks

      if (notSetViews > 0) {
        aggregatedDomains.push({
          domain: '(not set)',
          views: notSetViews,
          clicks: notSetClicks,
          ctr: notSetViews > 0 ? (notSetClicks / notSetViews) * 100 : 0
        })
      }
    }

    return aggregatedDomains
  }

  const aggregatedData = aggregateDomainData(placementData, campaignData)

  if (aggregatedData.length <= 0) {
    return <EmptyPromptComponent title={title} body='There is no domain data for the selected period.' />
  }

  const isInternetCampaign = aggregatedData.some(domain => domain.clicks > 0)

  const defaultSorting: ReportDataGridSorting[] = [
    {
      id: 'views',
      direction: 'desc'
    }
  ]

  const columns: ReportDataGridColumn[] = [
    {
      id: 'domain',
      displayAsText: 'Domain',
      schema: 'string'
    },
    {
      id: 'views',
      displayAsText: 'Views',
      schema: 'numeric',
      render: (value: number) => formatNumber(value, 0, 0)
    }
  ]

  if (isInternetCampaign) {
    columns.push(
      {
        id: 'clicks',
        displayAsText: 'Clicks',
        schema: 'numeric',
        render: (value: number) => formatNumber(value, 0, 0)
      },
      {
        id: 'ctr',
        displayAsText: 'CTR %',
        schema: 'numeric',
        render: (value: number) => formatPercentage(value / 100, 3, 3)
      }
    )
  }

  return (
    <EuiPanel>
      <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
        <EuiFlexItem grow={false}>
          <TitleComponent title={title} />
        </EuiFlexItem>{' '}
      </EuiFlexGroup>
      <EuiSpacer size='m' />
      <ReportDataGrid data={aggregatedData} columns={columns} defaultSorting={defaultSorting} />
    </EuiPanel>
  )
}

export default DomainPerformance
